import type {
  AuthUserDTO,
  BalancesResponse,
  CustomerBalanceResponse,
  BillDTO,
  BillsSummaryQuery,
  BillsSummaryResponse,
  BookingListItemDTO,
  CollectBalanceRequest,
  CollectBalanceResponse,
  WaiveBalanceRequest,
  CondimentAdminDTO,
  BankDepositDTO,
  CashBookQuery,
  CashBookResponse,
  CreateBankDepositRequest,
  CondimentDTO,
  CondimentSaleDTO,
  CondimentSalesSummaryQuery,
  CondimentSalesSummaryResponse,
  RecoveredQuery,
  RecoveredResponse,
  ReportQuery,
  ReportResponse,
  CreateCondimentSaleRequest,
  CreateBillRequest,
  CreateBillResponse,
  CreateBillsRequest,
  CreateBillsResponse,
  BookingShare,
  CreateBookingRequest,
  CreateCustomerRequest,
  CustomerDTO,
  DayLogsDTO,
  ExpenseDTO,
  ExpenseSummaryQuery,
  ExpenseSummaryResponse,
  HappyHourDTO,
  HappyHourDiscountResponse,
  ListBalancesQuery,
  ListBillsQuery,
  ListBookingsQuery,
  ListCounterLogsQuery,
  ListCondimentSalesQuery,
  ListCustomersQuery,
  ListExpensesQuery,
  ListPaymentsQuery,
  LoginOptionDTO,
  LoginRequest,
  LoginResponse,
  NextBillNumberResponse,
  Page,
  PaymentsResponse,
  SaveCondimentRequest,
  SaveDayLogsRequest,
  SaveExpenseRequest,
  SaveTableRequest,
  TableAdminDTO,
  TableDTO,
  UpdateBookingRequest,
  UpdateCondimentSaleRequest,
  UpdateCustomerRequest,
  WithdrawExpenseRequest,
} from '@six-pockets/shared';
import { http } from './http';

export { ApiRequestError, setAuth } from './http';

/** "?a=1&b=x" from the defined, non-empty values ("" when there are none). */
function toQueryString(params: object): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  return entries.length ? `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString()}` : '';
}

export const authApi = {
  /** Public list for the login page's dropdown. */
  logins: () => http.get<LoginOptionDTO[]>('/auth/logins'),
  /** Checks the password and returns an authToken (3 days) with the login and its module access. */
  login: (body: LoginRequest) => http.postWithAuth<LoginResponse>('/auth/login', body, null),
  /** Checks a stored authToken and returns its login. */
  me: (token: string) => http.getWithAuth<AuthUserDTO>('/auth/me', token),
  /** Ends the authToken on the server (other counters stay signed in). */
  logout: (token: string) => http.postWithAuth<void>('/auth/logout', {}, token),
};

export const tablesApi = {
  list: () => http.get<TableDTO[]>('/tables'),
  start: (tableId: string) => http.post<TableDTO>(`/tables/${tableId}/start`),
  /** A booking added after the fact: in play from a past start, or already ended (then priced). */
  addBooking: (tableId: string, body: CreateBookingRequest) => http.post<TableDTO>(`/tables/${tableId}/bookings`, body),
};

export type BookingAction = 'pause' | 'resume' | 'stop' | 'restart';

export const bookingsApi = {
  /** One page of bookings, earliest start first. Filtering, search and paging are done by the server. */
  list: (query: ListBookingsQuery, signal?: AbortSignal) =>
    http.get<Page<BookingListItemDTO>>(`/bookings${toQueryString(query)}`, signal),
  /** A typed name (it unlinks any customer the booking had). */
  rename: (bookingId: string, customerName: string) => http.patch<TableDTO>(`/bookings/${bookingId}`, { customerName }),
  /** An existing customer picked for the name: the booking takes their name. */
  setCustomer: (bookingId: string, customerId: string) => http.patch<TableDTO>(`/bookings/${bookingId}`, { customerId }),
  /** Name and/or times of an unbilled booking; the server re-prices a completed one. */
  update: (bookingId: string, body: UpdateBookingRequest) => http.patch<TableDTO>(`/bookings/${bookingId}`, body),
  act: (bookingId: string, action: BookingAction) => http.post<TableDTO>(`/bookings/${bookingId}/${action}`),
  /** Admin only, unbilled only. A soft delete: it leaves the app but stays in the database. */
  remove: (bookingId: string) => http.delete<TableDTO>(`/bookings/${bookingId}`),
};

export const condimentsApi = {
  list: () => http.get<CondimentDTO[]>('/condiments'),
};

/** Condiment tabs: managed on the Condiments page, ticked in the bill modal. */
export const condimentSalesApi = {
  /** One page of tabs, newest first. Filtering, search and paging are done by the server. */
  list: (query: ListCondimentSalesQuery = {}, signal?: AbortSignal) =>
    http.get<Page<CondimentSaleDTO>>(`/condiment-sales${toQueryString(query)}`, signal),
  summary: (query: CondimentSalesSummaryQuery = {}) => http.get<CondimentSalesSummaryResponse>(`/condiment-sales/summary${toQueryString(query)}`),
  create: (body: CreateCondimentSaleRequest) => http.post<CondimentSaleDTO>('/condiment-sales', body),
  update: (saleId: string, body: UpdateCondimentSaleRequest) => http.patch<CondimentSaleDTO>(`/condiment-sales/${saleId}`, body),
  remove: (saleId: string) => http.delete<void>(`/condiment-sales/${saleId}`),
};

export const customersApi = {
  /** One page of customers, A to Z by name. Search and paging are done by the server. */
  list: (query: ListCustomersQuery = {}, signal?: AbortSignal) => http.get<Page<CustomerDTO>>(`/customers${toQueryString(query)}`, signal),
  get: (id: string) => http.get<CustomerDTO>(`/customers/${id}`),
  create: (body: CreateCustomerRequest) => http.post<CustomerDTO>('/customers', body),
  update: (id: string, body: UpdateCustomerRequest) => http.put<CustomerDTO>(`/customers/${id}`, body),
  /** Admin only; refused (409) once the customer is on a bill. */
  remove: (id: string) => http.delete<void>(`/customers/${id}`),
};

export const billsApi = {
  /** One page of bills, newest first. Filtering, search and paging are done by the server. */
  list: (query: ListBillsQuery, signal?: AbortSignal) => http.get<Page<BillDTO>>(`/bills${toQueryString(query)}`, signal),
  /** Every bill with money due, grouped by customer. */
  balances: (query: ListBalancesQuery = {}, signal?: AbortSignal) =>
    http.get<BalancesResponse>(`/bills/balances${toQueryString(query)}`, signal),
  /** What one customer still owes on their bills (the bill modal's balance note). */
  customerBalance: (customerId: string, signal?: AbortSignal) =>
    http.get<CustomerBalanceResponse>(`/bills/balances/${customerId}`, signal),
  /** Table-time revenue (gross, net) on a day's bills: the cards on Bills home. */
  summary: (query: BillsSummaryQuery, signal?: AbortSignal) =>
    http.get<BillsSummaryResponse>(`/bills/summary${toQueryString(query)}`, signal),
  /** The balances recovered (Pay now) on a day, with the bills each one reached. */
  recovered: (query: RecoveredQuery, signal?: AbortSignal) =>
    http.get<RecoveredResponse>(`/bills/recovered${toQueryString(query)}`, signal),
  /** Admin: the payments received on a day, filtered by method, searched and paged by the server. */
  payments: (query: ListPaymentsQuery, signal?: AbortSignal) => http.get<PaymentsResponse>(`/payments${toQueryString(query)}`, signal),
  /** Collects a customer's balance, oldest bill first. */
  collect: (body: CollectBalanceRequest) => http.post<CollectBalanceResponse>('/bills/collect', body),
  /** Pay now with status Waived: lets off part or all of a balance (oldest bill first) and records it as an expense. */
  waive: (body: WaiveBalanceRequest) => http.post<CollectBalanceResponse>('/bills/waive', body),
  nextNumber: () => http.get<NextBillNumberResponse>('/bills/next-number'),
  /** What happy hour gives these bookings (0 when none of their time was in it). */
  happyHourDiscount: (bookingIds: string[], shares: BookingShare[] = []) =>
    http.post<HappyHourDiscountResponse>('/bills/happy-hour-discount', { bookingIds, shares }),
  create: (body: CreateBillRequest) => http.post<CreateBillResponse>('/bills', body),
  /** Several bills together, all or none (a booking split between teams). */
  createBatch: (body: CreateBillsRequest) => http.post<CreateBillsResponse>('/bills/batch', body),
};

/** Admin: Manage tables and Manage condiments. delete archives the record. */
export const manageApi = {
  tables: {
    list: () => http.get<TableAdminDTO[]>('/admin/tables'),
    create: (body: SaveTableRequest) => http.post<TableAdminDTO>('/admin/tables', body),
    update: (id: string, body: SaveTableRequest) => http.put<TableAdminDTO>(`/admin/tables/${id}`, body),
    remove: (id: string) => http.delete<void>(`/admin/tables/${id}`),
  },
  condiments: {
    list: () => http.get<CondimentAdminDTO[]>('/admin/condiments'),
    create: (body: SaveCondimentRequest) => http.post<CondimentAdminDTO>('/admin/condiments', body),
    update: (id: string, body: SaveCondimentRequest) => http.put<CondimentAdminDTO>(`/admin/condiments/${id}`, body),
    remove: (id: string) => http.delete<void>(`/admin/condiments/${id}`),
  },
  /** Settings → Happy hour. */
  happyHour: {
    get: (signal?: AbortSignal) => http.get<HappyHourDTO>('/admin/happy-hour', signal),
    save: (body: HappyHourDTO) => http.put<HappyHourDTO>('/admin/happy-hour', body),
  },
};

/** Admin: revenue (by bill date) and collection (by payment time) for a day, a month, or a range of them. */
export const reportsApi = {
  get: (query: ReportQuery, signal?: AbortSignal) => http.get<ReportResponse>(`/reports${toQueryString(query)}`, signal),
};

/** Cash book: the passbook of the cash counter, the day's cash and UPI logs, and deposits to the bank. */
export const cashBookApi = {
  /** The cards and the passbook lines for a range of months (not paged). */
  get: (query: CashBookQuery, signal?: AbortSignal) => http.get<CashBookResponse>(`/cash-book${toQueryString(query)}`, signal),
  logs: (query: ListCounterLogsQuery, signal?: AbortSignal) =>
    http.get<Page<DayLogsDTO>>(`/counter-logs${toQueryString(query)}`, signal),
  /** What is logged for one day (YYYY-MM-DD). */
  day: (day: string, signal?: AbortSignal) => http.get<DayLogsDTO>(`/counter-logs/${day}`, signal),
  /** Logs or corrects a day's cash count and/or UPI count. `day` is YYYY-MM-DD, or 'today' (the server's day). */
  saveDay: (day: string, body: SaveDayLogsRequest) => http.put<DayLogsDTO>(`/counter-logs/${day}`, body),
  /** Refused (409) above the money at the counter. */
  deposit: (body: CreateBankDepositRequest) => http.post<BankDepositDTO>('/bank-deposits', body),
};

/** Expense ledger: expenses, and withdrawals of the ones paid by someone. Withdrawn expenses can't change (409). */
export const expensesApi = {
  list: (query: ListExpensesQuery = {}, signal?: AbortSignal) => http.get<Page<ExpenseDTO>>(`/expenses${toQueryString(query)}`, signal),
  summary: (query: ExpenseSummaryQuery, signal?: AbortSignal) =>
    http.get<ExpenseSummaryResponse>(`/expenses/summary${toQueryString(query)}`, signal),
  create: (body: SaveExpenseRequest) => http.post<ExpenseDTO>('/expenses', body),
  update: (id: string, body: SaveExpenseRequest) => http.patch<ExpenseDTO>(`/expenses/${id}`, body),
  remove: (id: string) => http.delete<void>(`/expenses/${id}`),
  /** The payer takes the amount back, from the cash counter (a line in the cash book) or the bank. */
  withdraw: (id: string, body: WithdrawExpenseRequest) => http.post<ExpenseDTO>(`/expenses/${id}/withdraw`, body),
};
